import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { bcSessionKeys } from 'common/common/constants/session-keys';
import { ActionPermissionType } from 'common/common/constants/permission-type-enum';
import { BcRestApi } from 'common/api/rest-api';
import { CoolSessionStorage } from '@angular-cool/storage';
import { RawUserPermissionModel, UserPermissionModel } from './user-permission.model';
import { AccountPermissionType } from 'common/common/constants/account-permission-type';

@Injectable()
export class UserPermissionService {

  private _defaultActionPermissions: string[];
  private _cachedActionPermissions: RawUserPermissionModel[];

  private _defaultAccountPermissions: string[];
  private _cachedAccountPermissions: RawUserPermissionModel[];

  constructor(private bcRestApi: BcRestApi,
              private _sessionStorage: CoolSessionStorage) {
    this._defaultActionPermissions = ActionPermissionType.names();
    this._defaultAccountPermissions = AccountPermissionType.names();
  }

  public async getPermissionValues(): Promise<void[]> {
    this._cachedAccountPermissions = await this.bcRestApi.getOperatorAccountPermissionsMap(this._defaultAccountPermissions);
    this._cachedActionPermissions = await this.bcRestApi.getOperatorActionPermissionsMap(this._defaultActionPermissions);

    return Promise.all([
      this._sessionStorage.setObject(bcSessionKeys.storage.userPermissions, this._createPermissionMap(this._cachedActionPermissions)),
      this._sessionStorage.setObject(bcSessionKeys.storage.accountPermissions, this._createPermissionMap(this._cachedAccountPermissions))
    ]);
  }

  public hasPermissionFor(askedPermission: ActionPermissionType): boolean {
    let storedPermissions = this._sessionStorage.tryGetObject<string[]>(bcSessionKeys.storage.userPermissions);
    return storedPermissions && storedPermissions[ActionPermissionType[askedPermission]];
  }

  public async hasPermissionForAsync(askedPermission: ActionPermissionType): Promise<boolean> {
    if (!this._cachedActionPermissions) {
      await this.getPermissionValues();
    }

    return this.hasPermissionFor(askedPermission);
  }

  public hasAccountPermissionFor(askedPermission: AccountPermissionType): boolean {
    let storedPermissions = this._sessionStorage.tryGetObject<string[]>(bcSessionKeys.storage.accountPermissions);
    return storedPermissions && storedPermissions[AccountPermissionType[askedPermission]];
  }

  public hasPermissionForActionTypes(askedPermissions: ActionPermissionType[]): boolean {
    return askedPermissions.every((askedPermission) => this.hasPermissionFor(askedPermission));
  }

  public hasPermissionForAccountPermissionTypes(askedPermissions: AccountPermissionType[]): boolean {
    return askedPermissions.every((askedPermission) => this.hasAccountPermissionFor(askedPermission));
  }

  private _createPermissionMap(rawPermissions: RawUserPermissionModel[]): UserPermissionModel {
    return _.reduce(rawPermissions, (result, value) => this._mapSinglePermissionItem(result, value), {});
  }

  private _mapSinglePermissionItem(result, value): UserPermissionModel {
    result[value.permission] = value.enabled;
    return result;
  }
}
